<template>
  <!--
    🔴 **A SELECT, BECAUSE A COUNTRY HAS MORE THAN TWO LANGUAGES** (D52).

    Devin: *"make the Language selection a drop down because there can be many languages — in South
    Africa alone there are 11 official languages."* This shipped as one pill per locale, and its own
    note said what to do next: *"a two-item control reads faster than a select and needs no menu;
    past a handful of locales this becomes a menu."* Eleven is past a handful.

    🔴 **A NATIVE `<select>`, not a second overlay.** This control lives inside the user menu, which
    is itself a teleported overlay; a menu inside a menu has to solve focus, dismissal and z-order all
    over again, and does it worse than the browser. The native one is keyboard-navigable, type-ahead
    searchable — which is how you find isiXhosa in a list of eleven — and works identically on a
    phone, where it becomes the platform's own wheel.

    🔴 **No flags.** Metaship's own control shows one, and it is wrong for this product: all eleven of
    South Africa's official languages are South African, so every row would carry the same 🇿🇦. A flag
    is a country; a language is not. The name in its own language (locales.ts's endonyms) is what a
    person scans for anyway — you find "isiZulu" by looking for "isiZulu".
  -->
  <label class="sh-locale">
    <span class="sh-locale__sr">{{ t("locale.label") }}</span>
    <select class="sh-locale__select" :value="locale" data-testid="locale-select" @change="onChange">
      <option v-for="code in locales" :key="code" :value="code" :lang="code">{{ names[code] }}</option>
    </select>
    <!-- Drawn in CSS, not as an icon component: this control renders on the pre-auth page, inside
         the user menu, and in a bare unit test, and none of those should have to supply an icon
         library to draw one triangle. -->
    <span class="sh-locale__chev" aria-hidden="true"></span>
  </label>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { useI18n } from "vue-i18n";
import { useShellLocale } from "./locale";
import { isShellLocale, type ShellLocale } from "./locales";

/** "af-ZA" → "AF": the language, not the region — the region is the same for both. */
export function shortCode(locale: ShellLocale): string {
  return (locale.split("-")[0] ?? locale).toUpperCase();
}

export default defineComponent({
  name: "LocaleSwitch",
  setup() {
    const { t } = useI18n();
    const { locale, locales, names, setLocale } = useShellLocale();

    /**
     * A `<select>` hands back a string. It is validated rather than cast: the only values it can
     * carry are the ones rendered above, but an assertion here would be the one place a future
     * `?locale=` sync could put an unknown tag into the runtime.
     */
    function onChange(event: Event): void {
      const value = (event.target as HTMLSelectElement | null)?.value;
      if (isShellLocale(value)) setLocale(value);
    }

    return { t, locale, locales, names, setLocale, onChange };
  },
});
</script>
